import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useCallback, useMemo, useRef, useState, type FC, useEffect } from "react";
import { ChatMessages } from "@/components/ai/chat/ChatMessages";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/flexkit/ResizablePanel";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { cn } from "@/components/podkit/lib/cn";
import { Button } from "@/components/flexkit/Button";
import { useEscapeShortcut } from "@/hooks/use-escape-shortcut";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { ChatInput, type NewPendingMessage } from "@/components/ai/chat/ChatInput";
import { VSCodeIPC } from "@/vscode-ipc";
import { useTheme } from "@/hooks/use-theme";

import {
    useStreamConversation,
    type AgentMessageFragment,
    type Message,
    type AgentMessageFragmentThinking,
    parseFilePath,
} from "@/hooks/use-stream-conversation";
import { AgentExecutionDropdownMenu } from "@/components/ai/AgentExecutionDropdownMenu";
import { agentExecutionName } from "@/utils/agent-executions";
import { AgentExecutionActiveEnvironment } from "@/components/ai/chat/AgentExecutionActiveEnvironment";
import { Text } from "@/components/podkit/typography/Text";
import { IconLeftPanel } from "@/assets/icons/geist/IconLeftPanel";
import { IconRightPanel } from "@/assets/icons/geist/IconRightPanel";
import { Banner } from "@/components/flexkit/Banner";
import { formatError } from "@/utils/errors";
import { useEditorURLQuery, useListEditors } from "@/queries/editor-queries";
import { useListEnvironments } from "@/queries/environment-queries";
import { Switch } from "@/components/podkit/switch/Switch";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { usePendingMessages } from "@/hooks/use-pending-messages";

export const AgentExecutionConversation: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [sidebarContent, setSidebarContent] = useState<AgentMessageFragment | undefined>(undefined);
    const [sidebarViewMode, setSidebarViewMode] = useState<SidebarViewMode>("description");
    const panelRef = useRef<ImperativePanelHandle>(null);
    const vscodeIpcRef = useRef<VSCodeIPC | null>(null);

    const { pendingMessages, handleNewMessage } = usePendingMessages();
    const { error, messages, countOfUserBlocks, loadingMessage } = useStreamConversation(
        agentExecution.id,
        agentExecution?.status?.conversationUrl,
        pendingMessages,
    );

    // Update sidebar content when new thinking content arrives
    useEffect(() => {
        if (!sidebarContent || sidebarContent.type !== "thinking" || !messages.length) {
            return;
        }

        // Get the latest agent message
        const latestAgentMessage = messages[messages.length - 1];
        if (latestAgentMessage.type !== "agent") {
            return;
        }

        // Find the latest thinking fragment that matches the selected one's ID
        const matchingThinkingFragment = latestAgentMessage.fragments.find(
            (f): f is AgentMessageFragmentThinking => f.type === "thinking" && f.id === sidebarContent.id,
        );

        if (matchingThinkingFragment) {
            setSidebarContent(matchingThinkingFragment);
        }
    }, [messages, sidebarContent]);

    const handleCollapseClick = useCallback(() => {
        if (!panelRef.current) {
            return;
        }
        panelRef.current.collapse();
    }, []);

    const handleExpandClick = useCallback(() => {
        if (!panelRef.current) {
            return;
        }
        panelRef.current.expand(40);
    }, []);

    const handleContentClick = useCallback(
        (content: AgentMessageFragment) => {
            const filePath = parseFilePath(content);
            if (filePath) {
                setSidebarViewMode("vscode");
                if (vscodeIpcRef.current) {
                    void vscodeIpcRef.current.openFile(filePath);
                }
            } else {
                setSidebarViewMode("description");
            }
            setSidebarContent(content);
            handleExpandClick();
        },
        [handleExpandClick],
    );

    useEscapeShortcut(() => {
        if (isSidebarOpen) {
            handleCollapseClick();
        }
    });

    const lastEnvironmentId = useMemo(() => {
        // TODO(ak) it is rather hack because we broke delivering user environment on time, remove after it is fixed
        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i];
            for (let j = message.fragments.length - 1; j >= 0; j--) {
                const fragment = message.fragments[j];
                if (fragment.type === "environmentCreation") {
                    return fragment.environmentId;
                }
            }
        }
        return agentExecution.status?.usedEnvironments[agentExecution.status?.usedEnvironments.length - 1]
            ?.environmentId;
    }, [agentExecution.status?.usedEnvironments, messages]);

    // Initialize VS Code instance that will be used across view changes
    const vscodeInstance = useMemo(
        () => (
            <VSCodeBrowserView
                environmentId={lastEnvironmentId}
                vscodeIpcRef={vscodeIpcRef}
                currentFragment={sidebarContent}
            />
        ),
        [lastEnvironmentId, sidebarContent],
    );

    return (
        <ResizablePanelGroup direction="horizontal" className="rounded-lg">
            <ResizablePanel defaultSize={100} minSize={20} className="flex w-full flex-col">
                <ConversationHeader
                    agentExecution={agentExecution}
                    isSidebarOpen={isSidebarOpen}
                    onExpand={handleExpandClick}
                    onCollapse={handleCollapseClick}
                />
                {(error || loadingMessage) && (
                    <div className="flex flex-col gap-1">
                        {error && <Banner text={formatError(error)} variant="danger" className="mx-6 mb-0 mt-4" />}
                        {loadingMessage && (
                            <Text className="w-full text-center text-sm text-content-secondary">{loadingMessage}</Text>
                        )}
                    </div>
                )}
                <ConversationContent messages={messages} agentExecution={agentExecution} onClick={handleContentClick} />
                <ConversationInput
                    agentExecution={agentExecution}
                    onNewMessage={(message) => handleNewMessage(message, countOfUserBlocks)}
                />
            </ResizablePanel>
            <ResizableHandle disabled={!isSidebarOpen} />
            <ResizablePanel
                ref={panelRef}
                defaultSize={0}
                minSize={30}
                collapsible
                className={cn("flex flex-col transition-all duration-100 ease-in-out", {
                    "mb-[1px] mr-[1px] rounded-r-lg border-l-0.5 border-border-base": isSidebarOpen,
                    "bg-surface-02 text-content-secondary": sidebarViewMode === "description",
                    "bg-surface-01": sidebarViewMode === "vscode",
                })}
                onExpand={() => setIsSidebarOpen(true)}
                onCollapse={() => setIsSidebarOpen(false)}
                data-testid="agent-execution-conversation-sidebar"
            >
                <SidebarHeader
                    content={sidebarContent}
                    viewMode={sidebarViewMode}
                    onViewModeChange={setSidebarViewMode}
                />
                <div className="flex-1 overflow-hidden">
                    <div className={cn("h-full w-full", { hidden: sidebarViewMode !== "vscode" })}>
                        {vscodeInstance}
                    </div>
                    {sidebarViewMode === "description" && <SidebarContent content={sidebarContent} />}
                </div>
            </ResizablePanel>
        </ResizablePanelGroup>
    );
};

export type SidebarViewMode = "description" | "vscode";

export const VSCodeBrowserView: FC<{
    environmentId?: string;
    vscodeIpcRef: React.MutableRefObject<VSCodeIPC | null>;
    currentFragment?: AgentMessageFragment;
}> = ({ environmentId, vscodeIpcRef, currentFragment }) => {
    const { data: editors } = useListEditors();
    const { data: environmentsData } = useListEnvironments();
    const environment = useMemo(
        () => environmentsData?.environments.find((env) => env.id === environmentId),
        [environmentsData?.environments, environmentId],
    );
    const vscodeBrowserId = useMemo(
        () => editors?.editors.find((editor) => editor.alias === "vscode-browser")?.id,
        [editors?.editors],
    );
    const { data: editorUrl } = useEditorURLQuery({
        editorId: vscodeBrowserId,
        environment: environment,
    });

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const api = useGitpodAPI();
    const { effectiveTheme } = useTheme();

    const handleIframeLoad = useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe) {
            return;
        }
        const contentWindow = iframe.contentWindow;
        if (!contentWindow) {
            return;
        }
        if (vscodeIpcRef.current) {
            vscodeIpcRef.current.dispose();
        }
        vscodeIpcRef.current = new VSCodeIPC(api, contentWindow, effectiveTheme);
        const filePath = parseFilePath(currentFragment);
        if (filePath) {
            void vscodeIpcRef.current.openFile(filePath);
        }
    }, [api, vscodeIpcRef, currentFragment, effectiveTheme]);

    useEffect(() => {
        return () => {
            if (vscodeIpcRef.current) {
                vscodeIpcRef.current.dispose();
                vscodeIpcRef.current = null;
            }
        };
    }, [vscodeIpcRef]);

    if (!editorUrl?.url) {
        return null;
    }

    const src = editorUrl.url;

    return (
        <iframe
            ref={iframeRef}
            src={src}
            className="h-full w-full border-none"
            allow="clipboard-read; clipboard-write; keyboard-map;"
            data-testid="vscode-browser-iframe"
            onLoad={handleIframeLoad}
        />
    );
};

export const SidebarHeader: FC<{
    content?: AgentMessageFragment;
    viewMode: SidebarViewMode;
    onViewModeChange: (mode: SidebarViewMode) => void;
}> = ({ content, viewMode, onViewModeChange }) => {
    const text = useMemo(() => {
        if (!content) {
            return "No action selected";
        }
        switch (content.type) {
            case "action":
                return "Action";
            case "fileModification":
                return "File modification";
            case "environmentCreation":
                return "Environment creation";
            case "thinking":
                return "Thoughts";
            default:
                return `Unknown: ${content.type}`;
        }
    }, [content]);

    return (
        <div className="flex h-12 w-full items-center justify-between border-b border-border-base px-6 py-3">
            <Heading1 className="shrink-0 text-lg">{text}</Heading1>
            <div className="flex items-center gap-2">
                <Text className="text-sm">VS Code Browser</Text>
                <Switch
                    checked={viewMode === "vscode"}
                    onCheckedChange={(checked) => onViewModeChange(checked ? "vscode" : "description")}
                    aria-label="Toggle VS Code Browser"
                />
            </div>
        </div>
    );
};

export const SidebarContent: FC<{
    content?: AgentMessageFragment;
}> = ({ content }) => {
    const children = useMemo(() => {
        if (!content) {
            return <div className="font-mono text-sm">No content</div>;
        }
        switch (content.type) {
            case "action":
                return (
                    <div className="flex flex-col gap-2">
                        <div>
                            <Text className="text-sm font-bold">Intent</Text>
                            <div className="font-mono text-sm">{content.intentDescription}</div>
                        </div>
                        <div>
                            <Text className="text-sm font-bold">Result</Text>
                            <div className="font-mono text-sm">{content.resultDescription || "No result"}</div>
                        </div>
                    </div>
                );
            case "fileModification":
                return <div>{content.content}</div>;
            case "environmentCreation":
                return <div>{content.environmentId}</div>;
            case "thinking":
                return (
                    <div className="flex flex-col gap-2">
                        <div className="whitespace-pre-wrap font-mono text-sm">{content.content}</div>
                    </div>
                );
            default:
                return <div>{content.type}</div>;
        }
    }, [content]);

    return (
        <ScrollArea orientation="both">
            <div className="px-6 py-3">{children}</div>
        </ScrollArea>
    );
};

const ConversationHeader: FC<{
    agentExecution: PlainAgentExecution;
    isSidebarOpen: boolean;
    onExpand: () => void;
    onCollapse: () => void;
}> = ({ agentExecution, isSidebarOpen, onExpand, onCollapse }) => {
    const name = useMemo(() => agentExecutionName(agentExecution), [agentExecution]);
    return (
        <div className="flex h-12 w-full items-center justify-between gap-2 border-b border-border-base px-6 py-3">
            <div className="flex min-w-10 shrink items-center gap-1">
                <Heading1 className="truncate text-lg">{name}</Heading1>
                <AgentExecutionDropdownMenu agentExecution={agentExecution} />
            </div>
            <div className="flex min-w-14 shrink items-center justify-end gap-1">
                <AgentExecutionActiveEnvironment agentExecution={agentExecution} />
                <Button
                    variant="ghost"
                    className="rounded-[4px] border-0 p-0"
                    size="lg"
                    onClick={isSidebarOpen ? onCollapse : onExpand}
                    LeadingIcon={isSidebarOpen ? IconRightPanel : IconLeftPanel}
                />
            </div>
        </div>
    );
};

export const ConversationContent: FC<{
    messages: Message[];
    agentExecution: PlainAgentExecution;
    onClick?: (content: AgentMessageFragment) => void;
}> = ({ messages, agentExecution, onClick }) => {
    const contentRef = useRef<HTMLDivElement>(null);

    return (
        <ScrollArea
            orientation="vertical"
            autoScroll={true}
            autoScrollContentRef={contentRef}
            className="mx-[2px] my-[2px] h-full pl-[var(--scrollbar-width)]"
        >
            <div ref={contentRef}>
                <ChatMessages agentExecution={agentExecution} messages={messages} onClick={onClick} />
            </div>
        </ScrollArea>
    );
};

const ConversationInput: FC<{
    agentExecution: PlainAgentExecution;
    onNewMessage: (message: NewPendingMessage) => void;
}> = ({ agentExecution, onNewMessage }) => {
    return (
        <div className="m-6 mt-0">
            <div className="bg-surface-default sticky bottom-0">
                <div className="relative flex w-full justify-center">
                    <div className="absolute bottom-full h-8 w-full bg-gradient-to-t from-surface-01 to-transparent" />
                    <ChatInput
                        agentExecution={agentExecution}
                        onNewMessage={onNewMessage}
                        placeholder="Reply to Ona"
                        className="max-w-[700px] drop-shadow-[0_20px_20px_rgba(0,0,0,0.10)]"
                    />
                </div>
            </div>
        </div>
    );
};
